/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useRouter } from 'next/router';

import { IStore } from '~/interfaces/IStore';

type TTrackEvent =
  | 'AddPaymentInfo'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Donate'
  | 'FindLocation'
  | 'InitiateCheckout'
  | 'Lead'
  | 'Purchase'
  | 'Schedule'
  | 'Search'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Subscribe'
  | 'ViewContent';
interface IFacebookPixelContextData {
  track(event: TTrackEvent, params?: Record<string, any>): void;
}

const FacebookPixelContext = createContext<IFacebookPixelContextData>(
  {} as IFacebookPixelContextData
);

interface FacebookPixelProviderProps {
  store?: IStore;
}

const FacebookPixelProvider: FC<FacebookPixelProviderProps> = ({
  children,
  store: _store,
}) => {
  const router = useRouter();

  const [store, setStore] = useState<IStore | undefined>(_store);

  useEffect(() => {
    if (_store) setStore(_store);
  }, [_store]);

  const init = useCallback(() => {
    if (!store?.facebookPixelId) return;

    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        ReactPixel.init(store!.facebookPixelId!, undefined, {
          autoConfig: true,
          debug: false,
        });

        ReactPixel.pageView();
      });
  }, [store]);

  useEffect(() => {
    if (!store?.facebookPixelId) {
      return () => null;
    }

    router.events.on('routeChangeComplete', init);

    return () => {
      router.events.off('routeChangeComplete', init);
    };
  }, [router, init, store]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const track = useCallback(
    (event: TTrackEvent, params?: Record<string, any>) => {
      if (!store?.facebookPixelId) return;

      import('react-facebook-pixel')
        .then((x) => x.default)
        .then((ReactPixel) => {
          ReactPixel.init(store!.facebookPixelId!, undefined, {
            autoConfig: true,
            debug: false,
          });

          ReactPixel.track(event, params);
        });
    },
    [store]
  );

  return (
    <FacebookPixelContext.Provider
      value={{
        track,
      }}
    >
      {children}
    </FacebookPixelContext.Provider>
  );
};

function useFacebookPixel(): IFacebookPixelContextData {
  return useContext(FacebookPixelContext);
}

export { FacebookPixelProvider, useFacebookPixel };
